import type { Dictionary } from './en.ts';

export const de: Dictionary = {
  common: {
    tagline: 'Sprachübersetzer in Echtzeit',
    interfaceLanguage: 'Sprache der Oberfläche',
  },

  login: {
    title: 'Anmelden',
    lede: 'Geben Sie den Zugangscode für die Demo ein. Dieser Browser merkt ihn sich 12 Stunden lang.',
    submit: 'Anmelden',
    checking: 'Wird geprüft…',
    wrong: 'Der Code ist falsch. Bitte prüfen und erneut versuchen.',
    failed: 'Anmelden fehlgeschlagen. Bitte gleich erneut versuchen.',
  },

  home: {
    title: 'Ein Gespräch in zwei Sprachen',
    lede: 'Jeder spricht seine eigene Sprache und hört die andere übersetzt. Zwei Personen pro Raum, ohne Anmeldung.',
    myLanguage: 'Meine Sprache',
    peerLanguage: 'Ihre Sprache',
    accessCode: 'Zugangscode für die Demo',
    signOut: 'Abmelden',
    samePair: 'Wählen Sie zwei verschiedene Sprachen — sonst gibt es nichts zu übersetzen.',
    create: 'Raum erstellen',
    creating: 'Wird erstellt…',
    createFailed: 'Der Raum konnte nicht erstellt werden',

    readyTitle: 'Der Raum ist bereit',
    youHear: (language: string) => `Sie hören: ${language}.`,
    theyHear: (language: string) => `Die andere Person hört: ${language}.`,
    inviteLabel: 'Einladungslink — schicken Sie ihn der anderen Person',
    copy: 'Kopieren',
    copied: 'Kopiert',
    inviteNote:
      'Der Link gilt 15 Minuten und lässt genau eine Person herein. Veröffentlichen Sie ihn nirgends — er ist der Schlüssel zum Raum.',
    sourceSubtitles: 'Auch den Originaltext anzeigen',
    sourceSubtitlesOn:
      'Sie sehen neben der Übersetzung, was die andere Person tatsächlich gesagt hat, in ihrer eigenen Sprache. Der Anbieter berechnet das gesondert, das Gespräch wird also teurer.',
    sourceSubtitlesOff:
      'Es wird nur die Übersetzung angezeigt. In der Originalsprache wird nichts transkribiert, und es fallen keine zusätzlichen Kosten an.',
    sourceSubtitlesReady: 'Der Originaltext wird ebenfalls angezeigt.',
    goToCheck: 'Ton prüfen',
  },

  voice: {
    you: 'Sie',
    peer: 'Die andere Person',
    language: 'Sprache',
    provider: 'Sprachausgabe',
    openaiNote: 'eingebaute Stimme, sofort zu hören',
    elevenlabsNote: 'klarere Stimme, beginnt nach dem Satzende',
    noVoices: 'keine Stimmen für diese Sprache',
    voice: 'Stimme',
    builtIn: 'Eingebaute OpenAI-Stimme',
    loading: 'Stimmen werden geladen…',
    failed: 'Die Stimmen konnten nicht geladen werden. Bitte später erneut versuchen.',
    hintBuiltIn: 'Eine Stimme für alle Sprachen, wie bisher.',
    hintLibrary: 'Eine Stimme aus der ElevenLabs-Bibliothek.',
    groupVerified: 'Für diese Sprache geprüft',
    groupOther: 'Weitere Stimmen',
    accent: 'Diese Stimme ist für diese Sprache nicht geprüft und kann mit Akzent klingen.',
    noSample: 'Für diese Stimme gibt es keine Hörprobe',
    listen: 'Anhören',
    stop: 'Stoppen',
    needVoice:
      'Wählen Sie für jede Seite mit ElevenLabs eine Stimme oder stellen Sie sie auf OpenAI zurück.',
  },

  join: {
    checking: 'Die Einladung wird geprüft…',
    title: 'Einladung zu einem Gespräch',
    youWillHear: (language: string) => `Sie werden hören: ${language}.`,
    theyHear: (language: string) => `Die andere Person hört: ${language}.`,
    connect: 'Beitreten',
    connecting: 'Beitritt läuft…',
    full: 'Der Raum ist voll.',
    failedTitle: 'Beitritt nicht möglich',
    createYourOwn: 'Eigenen Raum erstellen',
    noInvite: 'In diesem Link steckt keine Einladung. Fragen Sie noch einmal danach.',
    unavailable: 'Dieser Raum ist nicht verfügbar',
    joinFailed: 'Beitritt fehlgeschlagen',
  },

  check: {
    title: 'Tonprüfung',
    lede: 'Setzen Sie Kopfhörer auf: ohne sie geht die Übersetzung zurück in Ihr Mikrofon und die andere Person hört ein Echo.',
    microphone: 'Mikrofon',
    defaultMic: 'Standardmikrofon',
    level: 'Pegel — sagen Sie etwas',
    applied: (features: string) => `Ihr Browser hat angewendet: ${features}.`,
    appliedNone: 'keine Bearbeitung',
    echoCancellation: 'Echounterdrückung',
    noiseSuppression: 'Rauschunterdrückung',
    autoGain: 'automatische Aussteuerung',
    measureNote: 'Messen Sie mit aufgesetzten Kopfhörern.',
    testSound: 'Ton testen',
    testAgain: 'Ton erneut testen',
    start: 'Klingt gut, los geht’s',
    starting: 'Verbindung wird aufgebaut…',
    micDenied:
      'Der Zugriff auf das Mikrofon wurde verweigert. Erlauben Sie ihn in den Browsereinstellungen und laden Sie die Seite neu.',
    micNotFound: 'Kein Mikrofon gefunden. Schließen Sie eines an und laden Sie die Seite neu.',
    micFailed: 'Das Mikrofon konnte nicht geöffnet werden.',
  },

  call: {
    title: 'Gespräch',
    timeLeft: (time: string) => `· noch ${time}`,
    waitingPeer: 'Warten auf die andere Person. Schicken Sie ihr den Einladungslink.',
    autoplayBlocked: 'Ihr Browser hat die Tonwiedergabe blockiert.',
    enableSound: 'Ton einschalten',
    lagging:
      'Die Übersetzung hinkt hinterher. Sie können den Übersetzer neu starten — das unfertige Fragment geht dabei verloren.',
    translatorStopped: (reason: string) =>
      `Die Übersetzung wurde beendet: ${reason}. Das Gespräch läuft weiter — starten Sie den Übersetzer neu.`,
    translationVolume: 'Lautstärke der Übersetzung',
    hearOriginal: 'Die Originalstimme hören',
    muteMic: 'Mikrofon ausschalten',
    unmuteMic: 'Mikrofon einschalten',
    restart: 'Übersetzung neu starten',
    asleep:
      'Eine Minute lang hat niemand gesprochen, daher ist die Übersetzung pausiert, um Kosten zu sparen. Sie läuft von selbst weiter, sobald Ihr Gegenüber spricht.',
    wake: 'Jetzt fortsetzen',
    leave: 'Verlassen',
    peerMuted: 'Die andere Person hat ihr Mikrofon stummgeschaltet',

    metricConnect: 'Start des Übersetzers',
    metricFirst: 'Erste Übersetzung',
    metricLag: 'Geschätzte Verzögerung',
    metricRtt: 'RTT zum Anbieter',
    metricVoiceDelay: 'Verzögerung der Sprachausgabe',
    metricCost: 'Kosten bisher (Schätzung)',
    costBreakdown: (openai: string, voice: string, credits: number) =>
      `OpenAI ${openai} · ElevenLabs ${voice} · Credits: ${credits}`,
    costNote:
      'Veröffentlichte Preise aus den Servereinstellungen, nicht die Rechnung der Anbieter.',
    metricVoiceLead: 'ElevenLabs vs. OpenAI, Durchschnitt',
    voiceLeadEarlier: (seconds: string) => `${seconds} s früher`,
    voiceLeadLater: (seconds: string) => `${seconds} s später`,
    iHear: (voice: string) => `Ich höre: ${voice}`,
    peerHears: (voice: string) => `Die andere Person hört: ${voice}`,
    voiceFellBack: 'OpenAI (ElevenLabs hat nicht geantwortet)',

    endedTitle: 'Das Gespräch ist beendet',
    endedNewRoom: 'Erstellen Sie für ein neues Gespräch einen neuen Raum.',
    newRoom: 'Neuer Raum',
    rateTitle: 'Bewerten Sie das Gespräch',
  },

  closed: {
    'peer-left': 'Die andere Person hat das Gespräch beendet.',
    'time-limit': 'Das Zeitlimit der Demo wurde erreicht.',
    'peer-lost': 'Die andere Person war zu lange weg.',
    'server-shutdown': 'Der Server startet neu.',
    expired: 'Die Zeit des Raums ist abgelaufen.',
    unknown: 'Die Verbindung wurde geschlossen.',
    signalLost: 'Kein Kontakt mehr zum Server. Erstellen Sie einen neuen Raum.',
  },

  status: {
    serverConnecting: 'Server: verbindet',
    serverOpen: 'Server: verbunden',
    serverReconnecting: 'Server: verbindet neu',
    serverClosed: 'Server: getrennt',

    peerWaiting: 'Gegenüber: wartet',
    peerConnecting: 'Gegenüber: verbindet',
    peerConnected: 'Gegenüber: verbunden',
    peerReconnecting: 'Gegenüber: stellt wieder her',
    peerFailed: 'Gegenüber: keine Verbindung',
    peerGone: 'Gegenüber: weg',

    translatorIdle: 'Übersetzung für mich: nicht gestartet',
    translatorConnecting: 'Übersetzung für mich: startet',
    translatorReady: 'Übersetzung für mich: läuft',
    translatorReconnecting: 'Übersetzung für mich: stellt wieder her',
    translatorFailed: 'Übersetzung für mich: fehlgeschlagen',
    translatorClosed: 'Übersetzung für mich: gestoppt',
    translatorAsleep: 'Übersetzung für mich: pausiert',

    peerTranslatorReady: 'Übersetzung für die andere Person: läuft',
    peerTranslatorWaiting: 'Übersetzung für die andere Person: wartet',
    peerTranslatorAsleep: 'Übersetzung für die andere Person: pausiert',
    relayed: 'Route: über TURN',
  },

  subtitles: {
    them: 'Gegenüber',
    you: 'Sie',
    themEmpty: 'Die andere Person hat noch nichts gesagt',
    youEmpty: 'Sagen Sie etwas — Ihre Worte und deren Übersetzung erscheinen hier',
  },

  rating: {
    accuracy: 'Genauigkeit der Übersetzung',
    voice: 'Wie natürlich die Stimme klingt',
    comfort: 'Wie angenehm das Gespräch war',
    send: 'Bewertungen senden',
    thanks: 'Danke — die Bewertungen sind gespeichert.',
  },

  errors: {
    no_credit: 'das OpenAI-Konto hat kein Guthaben mehr — laden Sie es auf',
    rate_limited: 'der Übersetzungsdienst drosselt uns — versuchen Sie es in einer Minute erneut',
    unconfigured: 'auf dem Server ist kein Übersetzungsschlüssel hinterlegt',
    unreachable: 'der Server konnte den Übersetzungsdienst nicht erreichen',
    offline: 'der Server war nicht erreichbar — prüfen Sie Ihre Verbindung',
    session_expired: 'die Übersetzungssitzung ist abgelaufen — starten Sie die Übersetzung neu',
    room_closed: 'dieser Raum ist bereits geschlossen',
    bad_offer: 'der Browser hat ein ungültiges Verbindungsangebot erzeugt',
    provider_refused: (status: number) =>
      `der Übersetzungsdienst hat die Verbindung abgelehnt (${status})`,
    unknown: 'die Übersetzung konnte nicht gestartet werden',
  },

  banner: {
    providerUnconfigured:
      'Auf dem Server ist kein Schlüssel für einen Übersetzungsanbieter hinterlegt. Sie können einen Raum erstellen, aber es wird nichts übersetzt.',
  },
};
